import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TextEditor } from 'Core/components/editor';
import { FooterButton } from 'Core/components';
import CustomPropTypes from 'CustomPropTypes';
import { Table } from 'antd';
import { Link } from 'react-router-dom';
import { fetchMachineDetails } from './store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectMachineDetailsData } from './store/selectors';
import { useDocumentUpload } from '../../core/hooks';
import { showToast } from '../../core/redux/toast/slice';
import { addMachineMaintenanceDetails } from './store/slice';
import { ACTION_MODE } from '../../core/constants/constants';
import WebcamFileUpload from '../../core/components/webcamfileupload/webcam-file-upload';
import { Button } from 'react-bootstrap';
import WebcamFileUploadModal from '../../core/components/modal/webcam-multi-capture-modal';
import ViewAllImagesModal from '../../core/components/viewallimagesmodal/view-all-images-modal';
import SegmentedImagesModal from '../../core/components/modal/segmentedimagesmodal/segmented-images-modal';

const ignoreKeys = [
  'cellLocation',
  'cellNumber',
  'notes',
  'specSheetDocument',
  'serialPlateDocument',
  'serviceManualDocument',
  'serviceContactDocument',
  'oilUsedDocument',
  'serviceContractDocuments',
  'id',
  'totalCyclesSinceLastMaint',
  'totalCyclesToDate',
  'lastMaintenanceDate',
  'maintenanceIntervalCycles',
  'currentMaintenanceDate',

];

const maintenanceData = {
  date: new Date().toISOString().split('T')[0],
  location: '',
  lastServicedBy: '',
  lastServiceDate: new Date().toISOString().split('T')[0],
  specSheetDocument: '',
  serialPlateDocument: '',
  serviceManualDocument: '',
  oilUsedDocument: '',
  notes: '',
  cellLocation: '',
  machineNumber: '',
  totalCyclesSinceLastMaint:0,
  totalCyclesToDate:new Date().toISOString().split('T')[0],
  lastMaintenanceDate:new Date().toISOString().split('T')[0],
  maintenanceIntervalCycles:0,
  currentMaintenanceDate:new Date().toISOString().split('T')[0],
};

const stripHtml = html => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const MachinesAddEditForm = ({ data, onCloseModal, isEditMode, cellActionRecord }) => {
  const [formData, setFormData] = useState({});
  const dispatch = useDispatch();
  const [isEditable, setIsEditable] = useState(isEditMode);
  const detailsData = useSelector(selectMachineDetailsData);
  const fileInputRef = useRef(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { uploadDocuments, loading } = useDocumentUpload();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [contractFiles, setContractFiles] = useState([]);
  const [tempFile, setTempFile] = useState(null);
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [showImageViewerModal, setShowImageViewerModal] = useState(false);
  const [showManageImagesModal, setShowManageImagesModal] = useState(false);
  const [serialPlateDocuments, setSerialPlateDocuments] = useState([]);
  const [machineMaintenanceId, setMachineMaintenanceId] = useState(0);
  const [processedImageSegments, setProcessedImageSegments] = useState([]);

  useEffect(() => {
    if (cellActionRecord && cellActionRecord?.mode) {
      if (cellActionRecord.mode == ACTION_MODE.VIEW) {
        // setFormData(cellActionRecord.data);
        setFormData({
        ...maintenanceData,
        ...cellActionRecord.data,
        date: cellActionRecord.data?.date ?? maintenanceData.date,
      });
        setIsEditable(false);
      } else if (cellActionRecord.mode == ACTION_MODE.COPY_TO_CELL) {
        setFormData({
          ...cellActionRecord.data,
          id: 0,
          lastServicedBy: '',
          lastServiceDate: new Date().toISOString().split('T')[0],
        });
      }
    } else {
      setFormData({ ...data, ...maintenanceData });
    }
  }, [data, cellActionRecord]);

  // Process image segments when detailsData changes
  useEffect(() => {
    if (detailsData && detailsData.imageSegments) {
      console.log('Processing image segments from detailsData:', detailsData.imageSegments);
      const processed = processImageSegments(detailsData.imageSegments);
      setProcessedImageSegments(processed);
      
      // Update machineMaintenanceId if available
      if (detailsData.id) {
        setMachineMaintenanceId(detailsData.id);
      }
    }
  }, [detailsData]);

  // Process image segments when cellActionRecord changes (for VIEW mode from cell history)
  useEffect(() => {
    if (cellActionRecord && cellActionRecord.mode === ACTION_MODE.VIEW && cellActionRecord.data && cellActionRecord.data.imageSegments) {
      console.log('Processing image segments from cellActionRecord.data:', cellActionRecord.data.imageSegments);
      const processed = processImageSegments(cellActionRecord.data.imageSegments);
      setProcessedImageSegments(processed);
      
      // Update machineMaintenanceId if available
      if (cellActionRecord.data.id) {
        setMachineMaintenanceId(cellActionRecord.data.id);
      }
    }
  }, [cellActionRecord]);

  const handleEditClick = record => {
    setSelectedRecord(record);

    //if (record.documenttype === 'Service Contact') {
    if (
      record.documenttype === 'Service Contact' ||
      record.documenttype === 'Oil Used' ||
      record.documenttype === 'Service Contract'
    ) {
      setExpandedRowKeys([record.key]);
    } else {
      setExpandedRowKeys([]);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleViewClick = record => {
    if (
      record.documenttype === 'Spec Sheet' ||
      record.documenttype === 'Serial Plate' ||
      record.documenttype === 'Service Manual' ||
      record.documenttype === 'Service Contract'
    ) {
      const fileUrl = getDocument(record);
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      } else {
        dispatch(
          showToast({
            message: 'Document not available',
            type: 'warning',
          }),
        );
      }
    }
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (file && selectedRecord) {
      if (selectedRecord.documenttype === 'Service Contract') {
        setTempFile(file);
      } else {
        const docFormData = new FormData();
        docFormData.append('file', file);
        docFormData.append('fileCategoryId', file.documentName);
        docFormData.append('uploadCategory', `MACHINE`);
        const result = await uploadDocuments(docFormData);

        //   if (selectedRecord.documenttype == 'Spec Sheet') {
        //     setFormData({ ...formData, specSheetDocument: result.relativePath });
        //   } else if (selectedRecord.documenttype == 'Serial Plate') {
        //     setFormData({ ...formData, serialPlateDocument: result.relativePath });
        //   } else if (selectedRecord.documenttype == 'Service Manual') {
        //     setFormData({ ...formData, serviceManualDocument: result.relativePath });
        //   } else if (selectedRecord.documenttype == 'Oil Used') {
        //     setFormData({ ...formData, oilUsedDocument: result.relativePath });
        //   } else {
        //     e.target.value = null;
        //   }
        // }
        // };

        if (selectedRecord.documenttype == 'Spec Sheet') {
          setFormData({ ...formData, specSheetDocument: result.relativePath });
        } else if (selectedRecord.documenttype == 'Serial Plate') {
          setFormData({ ...formData, serialPlateDocument: result.relativePath });
        } else if (selectedRecord.documenttype == 'Service Manual') {
          setFormData({ ...formData, serviceManualDocument: result.relativePath });
        } else if (selectedRecord.documenttype == 'Oil Used') {
          setFormData({ ...formData, oilUsedDocument: result.relativePath });
        }
      }
    }
  };

  const handleAddContractFile = async () => {
    if (tempFile) {
      try {
        const docFormData = new FormData();
        docFormData.append('file', tempFile);
        docFormData.append('uploadCategory', 'MACHINE');
        const result = await uploadDocuments(docFormData);

        if (result && result.relativePath) {
          setContractFiles([
            ...contractFiles,
            {
              documentName: tempFile.name,
              filePath: result.relativePath,
              file: tempFile,
            },
          ]);
          setTempFile(null);
        } else {
          // If result doesn't have relativePath, show error instead of using blob URL
          console.error('Upload response missing relativePath:', result);
          dispatch(
            showToast({
              message: 'File upload failed: No file path returned from server.',
              type: 'danger',
            }),
          );
          setTempFile(null);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        // Show error to user
        dispatch(
          showToast({
            message: 'Error uploading file. Please try again.',
            type: 'danger',
          }),
        );
      }
    }
  };

  const handleViewImagesForSerialPlate = () => {
    setShowImageViewerModal(true);
  };

  const handleRemoveContractFile = index => {
    const newFiles = contractFiles.filter((_, i) => i !== index);
    setContractFiles(newFiles);
  };

  const getDocumentName = path => {
    return path.split('/').pop();
  };

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const getDocument = record => {
    if (record.documenttype == 'Spec Sheet') {
      return formData.specSheetDocument;
    } else if (record.documenttype == 'Serial Plate') {
      return formData.serialPlateDocument;
    } else if (record.documenttype == 'Service Manual') {
      return formData.serviceManualDocument;
    } else if (record.documenttype == 'Oil Used') {
      return formData.oilUsedDocument;
    } else {
      return '';
    }
  };

  const columns = [
    { title: 'Document Type', dataIndex: 'documenttype', key: 'documenttype' },
    { title: 'Format', dataIndex: 'format', key: 'format' },
    // {
    //   title: 'View',
    //   dataIndex: 'id',
    //   key: 'id',
    //   render: (text, record) => (
    //     <div className="action-table-data">
    //       <div className="edit-delete-action">
    //         <Link
    //           disabled={getDocument(record) == null}
    //           className="me-2 p-2"
    //           //  onClick={() => handleEditClick(record)}
    //           onClick={() => {
    //             setSelectedRecord(record);
    //             setExpandedRowKeys([record.key]);
    //             handleViewClick(record);
    //           }}
    //         >
    //           View
    //         </Link>
    //       </div>
    //     </div>
    //   ),
    // },

    {
      title: 'Manage Images',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {record.documenttype === 'Serial Plate' ? (
              <Link
                className="me-2 p-2"
                disabled={!(serialPlateDocuments && serialPlateDocuments.length > 0) && !(processedImageSegments && processedImageSegments.length > 0)}
                onClick={() => {
                  setSelectedRecord(record);
                  setExpandedRowKeys([record.key]);
                  setShowManageImagesModal(true);
                }}
              >
                Manage Images
              </Link>
            ) : (
              <Link
                disabled={getDocument(record) == null}
                className="me-2 p-2"
                onClick={() => {
                  setSelectedRecord(record);
                  setExpandedRowKeys([record.key]);
                  handleViewClick(record);
                }}
              >
                View
              </Link>
            )}
          </div>
        </div>
      ),
    },

    // {
    //   title: 'Action',
    //   dataIndex: 'id',
    //   key: 'id',
    //   render: (text, record) => (
    //     <div className="action-table-data">
    //       <div className="edit-delete-action">
    //         <Link
    //           disabled={!isEditable}
    //           className="me-2 p-2"
    //           onClick={() => isEditable && handleEditClick(record)}
    //         >
    //           <i data-feather="edit" className="feather-edit"></i>
    //           {['Service Contact', 'Oil Used'].includes(record.documenttype)
    //             ? 'Edit'
    //             : 'Edit/Upload'}
    //         </Link>
    //       </div>
    //     </div>
    //   ),
    // },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {record.documenttype === 'Serial Plate' ? (
              <Link
                disabled={!isEditable}
                className="me-2 p-2"
                style={{ minWidth: '140px', display: 'inline-block', textAlign: 'center' }}
                onClick={() => isEditable && setShowWebcamModal(true)}
              >
                <i className="feather-camera me-1" /> Capture Image
              </Link>
            ) : (
              <Link
                disabled={!isEditable}
                className="me-2 p-2"
                style={{ minWidth: '140px', display: 'inline-block', textAlign: 'center' }}
                onClick={() => isEditable && handleEditClick(record)}
              >
                <i data-feather="edit" className="feather-edit"></i>
                {['Service Contact', 'Oil Used'].includes(record.documenttype)
                  ? 'Edit'
                  : 'Edit/Upload'}
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];
  const testdata = [
    {
      key: 1,
      documenttype: 'Spec Sheet',
      format: 'PDF',
      name: 'Not Expandable',
    },
    {
      key: 2,
      documenttype: 'Serial Plate',
      format: 'Picture',
      name: 'Not Expandable',
    },
    {
      key: 3,
      documenttype: 'Service Manual',
      format: 'PDF',
      name: 'Not Expandable',
    },
    {
      key: 4,
      documenttype: 'Service Contact',
      format: 'List',
    },
    {
      key: 5,
      documenttype: 'Oil Used',
      format: 'List',
    },
    {
      key: 6,
      documenttype: 'Service Contract',
      format: 'PDF',
    },
  ];

  const valid = payload => {
    if (
      !payload ||
      Object.keys(payload).some(key => {
        return !ignoreKeys.includes(key) && !payload[key];
      })
    ) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    return true;
  };

  const handleClose = () => {
    onCloseModal();
  };

  const onSave = () => {
    const payload = {
      id: 0,
      userId: localStorage.getItem('UserId'),
      assetNumber: formData.assetNumber,
      modelNumber: formData.modelNumber,
      serialNumber: formData.serialNumber,
      date: formData.date,
      location: formData.location,
      lastServicedBy: formData.lastServicedBy,
      lastServiceDate: formData.lastServiceDate,
      specSheetDocument: formData.specSheetDocument,
      serialPlateDocument: formData.serialPlateDocument,
      serviceManualDocument: formData.serviceManualDocument,
      serviceContactDocument: formData.serviceContactDocument,
      oilUsedDocument: formData.oilUsedDocument,
      serviceContractDocuments: contractFiles,
      notes: stripHtml(formData.notes),
      cellLocation: formData.cellLocation,
      machineNumber: formData.machineNumber,
      machine: formData.assetNumber, // Add machine field using assetNumber
      totalCyclesSinceLastMaint: formData.totalCyclesSinceLastMaint,
      totalCyclesToDate: formData.totalCyclesToDate,
      lastMaintenanceDate:formData.lastMaintenanceDate,
      maintenanceIntervalCycles: String(formData.maintenanceIntervalCycles || 0), // Convert to string
      currentMaintenanceDate:formData.currentMaintenanceDate,
    };

    if (valid(payload)) {
      dispatch(addMachineMaintenanceDetails(payload));
    }
  };

  const convertDocumentsForWebCamFileUpload = inputId => {
    const matchedDocs = (formData.moldDocuments || [])
      .filter(doc => doc.documentName === inputId)
      .filter(doc => doc.documentName === inputId)
      .map(doc => {
        let fileName = '';

        if (doc.documentPath !== undefined) {
          const encodedFileName = doc.documentPath.split('/').pop();
          const decoded = decodeURIComponent(encodedFileName);
          const lastUnderscore = decoded.lastIndexOf('_');
          const extensionIndex = decoded.lastIndexOf('.');

          if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
            fileName = decoded.slice(0, lastUnderscore) + decoded.slice(extensionIndex);
          } else {
            fileName = decoded;
          }
        }

        return {
          id: doc.documentId,
          name: fileName,
          path: doc.documentPath,
        };
      });

    return matchedDocs;
  };

  const updateMachineDocuments = newFiles => {
    setFormData(prev => ({
      ...prev,
      serialPlateDocument: newFiles[0]?.filePath || '',
    }));
  };

  const handleSerialPlateUpload = uploadedFiles => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    // UploadedFiles must include { fileName, filePath, relativePath }

    const docs = uploadedFiles.map(file => ({
      name: file.fileName ?? 'Captured Image', // fallback
      path: file.filePath ?? file.relativePath ?? '', // fallback to relativePath
    }));

    // Update modal viewer
    setSerialPlateDocuments(docs);

    // Save in form data (comma-separated for backend)
    setFormData(prev => ({
      ...prev,
      serialPlateDocument: docs.map(doc => doc.path).join(','),
    }));
  };

  const handleMachineMaintenanceSaved = (maintenanceId) => {
    console.log('Machine maintenance saved with ID:', maintenanceId);
    setMachineMaintenanceId(maintenanceId);
  };

  // Process image segments from API response
  const processImageSegments = (imageSegments) => {
    if (!imageSegments || !Array.isArray(imageSegments)) return [];
    
    console.log('Processing image segments:', imageSegments);
    
    // Map image segments to the Serial Plate field
    const serialPlateSegments = imageSegments.filter(segment => 
      segment.record === 'Serial Plate' && segment.category === 'Machine-Maintenance'
    );
    
    if (serialPlateSegments.length > 0) {
      console.log('Found Serial Plate segments:', serialPlateSegments);
      return serialPlateSegments.map(segment => ({
        name: segment.title || `Image_${segment.id}`,
        path: segment.imageUrl,
        id: segment.id,
        segment: segment.segment || 'General',
        record: segment.record,
        title: segment.title,
        description: segment.description
      }));
    }
    
    return [];
  };

const parsedSerialPlateDocuments = formData.serialPlateDocument
  ? formData.serialPlateDocument.split(',').map(path => ({
      name: path.split('/').pop(),
      path,
    }))
  : [];

  return (
    <>
      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Asset Number <span className="text-danger">*</span>
            </label>
            <input
              name="assetNumber"
              type="text"
              className="form-control"
              value={formData.assetNumber}
              onChange={e => updateFormField('assetNumber', e.target.value)}
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={e => updateFormField('date', e.target.value)}
              className="form-control"
              disabled={!isEditable}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Machine Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.machineNumber}
              onChange={e => updateFormField('machineNumber', e.target.value)}
              className="form-control"
              disabled={!isEditable}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Serial Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.serialNumber}
              onChange={e => updateFormField('serialNumber', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Status</label>
            <input
              type="bool"
              value={formData.isDeleted}
              onChange={e => updateFormField('isDeleted', e.target.value)}
              className="form-control"
              disabled={!isEditable}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Lane</label>
            <input
              type="text"
              value={formData.lane}
              onChange={e => updateFormField('lane', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Location <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={e => updateFormField('location', e.target.value)}
              className="form-control"
              disabled={!isEditable}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Cell Location<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.cellLocation}
              onChange={e => updateFormField('cellLocation', e.target.value)}
              className="form-control"
              disabled={!isEditable}
              required
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Brand <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={e => updateFormField('brand', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Model Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.modelNumber}
              onChange={e => updateFormField('modelNumber', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Tonnage <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.tonnage}
              onChange={e => updateFormField('tonnage', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Serviced By <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.lastServicedBy}
              disabled={!isEditable}
              onChange={e => updateFormField('lastServicedBy', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Service Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              value={formData.lastServiceDate?.split('T')[0] || ''}
              disabled={!isEditable}
              onChange={e => updateFormField('lastServiceDate', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Version</label>
            <input
              type="text"
              value={formData.version}
              onChange={e => updateFormField('version', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
      </div>

      {/* Usage Information */}
      <h5 className="header-five">Usage Information</h5>
      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Total Cycles Since Last Maintenance</label>
            <input
              type="number"
              value={formData.totalCyclesSinceLastMaint}
              onChange={e => updateFormField('totalCyclesSinceLastMaint', e.target.value)}
              className="form-control"
              placeholder=""
              disabled={true}
            />
          </div>
        </div>

        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Total Cycles to Date</label>
            <input
              type="date"
              value={formData.totalCyclesToDate?.split('T')[0] || ''}
              onChange={e => updateFormField('totalCyclesToDate', e.target.value)}
              className="form-control"
              placeholder=""
              disabled={true}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Last Maintenance Date</label>
            <input
              type="date"
              value={formData.lastMaintenanceDate?.split('T')[0] || ''}
              onChange={e => updateFormField('lastMaintenanceDate', e.target.value)}
              className="form-control"
              placeholder=""
              disabled={true}
            />
          </div>
        </div>
      </div>

      {/* Maintenance Interval*/}
      <h5 className="header-five">Maintenance Interval</h5>
      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Maintenance Interval (Cycles)</label>
            <input
              type="number"
              value={formData.maintenanceIntervalCycles}
              onChange={e => updateFormField('maintenanceIntervalCycles', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>

        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Current Maintenance Date</label>
            <input
              type="date"
              value={formData.currentMaintenanceDate?.split('T')[0] || ''}
              onChange={e => updateFormField('currentMaintenanceDate', e.target.value)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <Table
            columns={columns}
            pagination={false}
            expandable={{
              expandedRowRender: record => {
                if (
                  record.documenttype === 'Service Contact' &&
                  selectedRecord?.key === record.key
                ) {
                  return (
                    <textarea
                      style={{ width: '100%' }}
                      rows={4}
                      value={formData.serviceContactDocument || ''}
                      disabled={!isEditable}
                      onChange={e => updateFormField('serviceContactDocument', e.target.value)}
                      placeholder="Enter service contact details"
                    />
                  );
                }

                if (record.documenttype === 'Oil Used' && selectedRecord?.key === record.key) {
                  return (
                    <textarea
                      style={{ width: '100%' }}
                      rows={4}
                      value={formData.oilUsedDocument || ''}
                      disabled={!isEditable}
                      onChange={e => updateFormField('oilUsedDocument', e.target.value)}
                      placeholder="Enter oil used details"
                    />
                  );
                }

                if (
                  record.documenttype === 'Service Contract' &&
                  selectedRecord?.key === record.key
                ) {
                  return (
                    <div className="p-3">
                      <div className="d-flex gap-3 align-items-center mb-3">
                        <input type="file" onChange={handleFileChange} className="form-control" />
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={handleAddContractFile}
                          disabled={loading || !tempFile}
                        >
                          {loading ? 'Uploading...' : 'Upload'}
                        </button>
                      </div>
                      {contractFiles.length > 0 && (
                        <div className="mt-3">
                          <h6>Uploaded Files:</h6>
                          <ul className="list-group">
                            {contractFiles.map((file, index) => (
                              <li
                                key={index}
                                className="list-group-item d-flex justify-content-between align-items-center"
                              >
                                <div>
                                  <strong>{file.documentName}</strong>
                                  <br />
                                  <small>{getDocumentName(file.filePath)}</small>
                                </div>
                                <div className="d-flex">
                                  <a
                                    href={file.filePath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-icon btn-sm btn-soft-success rounded-pill me-2"
                                  >
                                    <i className="feather-eye"></i>
                                  </a>
                                  {/* <a
                                    href="javascript:void(0);"
                                    className="btn btn-icon btn-sm btn-soft-success rounded-pill me-2"
                                  >
                                    <i className="feather-download"></i>
                                  </a> */}
                                  <a
                                    href="javascript:void(0);"
                                    onClick={() => handleRemoveContractFile(index)}
                                    className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                                  >
                                    <i className="feather-trash"></i>
                                  </a>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                }

                return <p style={{ margin: 0 }}>{record.description}</p>;
              },
              expandedRowKeys: expandedRowKeys,
              onExpand: (expanded, record) => {
                //if (expanded && record.documenttype === 'Service Contact') {
                if (
                  expanded &&
                  (record.documenttype === 'Service Contact' ||
                    record.documenttype === 'Oil Used' ||
                    record.documenttype === 'Service Contract')
                ) {
                  setExpandedRowKeys([record.key]);
                  setSelectedRecord(record);
                } else {
                  setExpandedRowKeys([]);
                }
              },
              rowExpandable: record => record.name !== 'Not Expandable',
            }}
            dataSource={testdata}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="input-blocks">
            <label>Notes</label>
            <TextEditor
              data={formData.notes || ''}
              disabled={!isEditable}
              onChange={event => {
                updateFormField('notes', event);
              }}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-lg-8"></div>
        <div className="col-12 col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Save Machine Maintenance"
            onSave={onSave}
            disableSave={!isEditable}
            onCancel={handleClose}
          />
        </div>
      </div>

      {showWebcamModal && (
        <WebcamFileUploadModal
          show={showWebcamModal}
          onClose={() => setShowWebcamModal(false)}
          onSubmit={handleSerialPlateUpload}
          context="machine"
          buttonLabel="Serial Plate"
          maintenanceData={formData}
          onMaintenanceSaved={handleMachineMaintenanceSaved}
        />
      )}

      {showImageViewerModal && (
        <ViewAllImagesModal
          show={showImageViewerModal}
          onClose={() => setShowImageViewerModal(false)}
          documents={parsedSerialPlateDocuments }
        />
      )}

      {showManageImagesModal && (
        <SegmentedImagesModal
          show={showManageImagesModal}
          onClose={() => setShowManageImagesModal(false)}
          documents={processedImageSegments.length > 0 ? processedImageSegments : serialPlateDocuments}
          moldInfoID={0}
          buttonLabel="Serial Plate"
          maintenanceId={machineMaintenanceId}
          context="machine"
        />
      )}
      
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ display: 'none' }}>
          <p>Debug Info:</p>
          <p>processedImageSegments: {JSON.stringify(processedImageSegments)}</p>
          <p>serialPlateDocuments: {JSON.stringify(serialPlateDocuments)}</p>
          <p>machineMaintenanceId: {machineMaintenanceId}</p>
          <p>detailsData.imageSegments: {JSON.stringify(detailsData?.imageSegments)}</p>
        </div>
      )}
    </>
  );
};

MachinesAddEditForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape(CustomPropTypes.machines),
  onChanges: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

export default MachinesAddEditForm;
